import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowDown, ArrowUpRight, Check } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import AnchorMark from "@/components/AnchorMark";

export default function ProgramDownload() {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("Please enter your email.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/program/subscribe", { email, name });
      setSuccess(true);
      toast.success(data.message || "You're in. Check your inbox.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="program"
      data-testid="program-section"
      className="relative py-24 md:py-40"
      style={{ backgroundColor: "#F7F5F0" }}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
        <div className="md:col-span-6 order-2 md:order-1">
          <p className="overline mb-6">07 — A Free Beginning</p>
          <h2
            className="serif text-4xl sm:text-5xl md:text-6xl text-[#15243D] leading-[1.05] tracking-tight"
            style={{ fontWeight: 300 }}
          >
            The 28-Day<br />
            <em className="italic text-[#C07865]">Anchor</em> Program.
          </h2>
          <p className="mt-8 text-lg font-light leading-relaxed text-[#5A5D63] max-w-lg">
            A quiet, four-week practice you can begin tonight. One short
            invitation each day — a question to sit with, a small action, a
            line to carry. Built from the same foundation as the full coaching
            work.
          </p>

          <ul className="mt-8 space-y-3 max-w-md">
            {[
              "Daily reflections (5 minutes)",
              "Four weekly themes",
              "One simple practice to carry through your day",
            ].map((item) => (
              <li
                key={item}
                className="flex items-start gap-3 text-base font-light text-[#15243D]"
              >
                <Check
                  size={18}
                  strokeWidth={1.5}
                  className="text-[#C07865] mt-0.5 flex-shrink-0"
                />
                {item}
              </li>
            ))}
          </ul>

          <div className="mt-10">
            <Link
              to="/program"
              data-testid="program-read-online-button"
              className="btn-secondary"
            >
              Read the program online
              <ArrowUpRight size={16} strokeWidth={1.5} />
            </Link>
          </div>

          {!success ? (
            <form
              onSubmit={submit}
              className="mt-12 space-y-6 max-w-md"
              data-testid="program-form"
            >
              <div>
                <label className="text-xs tracking-[0.18em] uppercase text-[#15243D] mb-2 block">
                  First name <span className="text-[#8A8D93] normal-case tracking-normal">(optional)</span>
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                  className="anchor-input"
                  data-testid="program-name-input"
                />
              </div>
              <div>
                <label className="text-xs tracking-[0.18em] uppercase text-[#15243D] mb-2 block">
                  Email
                </label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="anchor-input"
                  data-testid="program-email-input"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="btn-primary"
                data-testid="program-submit-button"
              >
                {loading ? "Sending…" : "Send me the program"}
                <ArrowDown size={16} strokeWidth={1.5} />
              </button>
              <p className="text-xs text-[#8A8D93] leading-relaxed">
                We’ll email you a copy. Prefer to read it now? You can also{" "}
                <Link
                  to="/program"
                  className="text-[#C07865] hover:text-[#A66352] underline-offset-4 hover:underline"
                  data-testid="program-read-online-inline"
                >
                  begin online
                </Link>
                .
              </p>
            </form>
          ) : (
            <div
              className="mt-12 max-w-md border-l-2 border-[#C07865] pl-6 py-2"
              data-testid="program-success"
            >
              <p
                className="serif text-2xl text-[#15243D] mb-3"
                style={{ fontWeight: 300 }}
              >
                Welcome. Day one begins quietly.
              </p>
              <p className="text-base font-light text-[#5A5D63]">
                The 28-day program is on its way to{" "}
                <span className="text-[#15243D]">{email}</span>. If it doesn’t
                arrive within a few minutes, check your other inbox folders.
              </p>
              <div className="mt-8">
                <Link
                  to="/program/day/1"
                  className="btn-primary"
                  data-testid="program-begin-now-button"
                >
                  Begin Day 1 now
                  <ArrowUpRight size={16} strokeWidth={1.5} />
                </Link>
              </div>
            </div>
          )}
        </div>

        <div className="md:col-span-5 md:col-start-8 order-1 md:order-2">
          <div
            className="relative aspect-[4/5] w-full overflow-hidden flex items-center justify-center"
            style={{ backgroundColor: "#EBE8E0" }}
          >
            <AnchorMark size={220} color="#15243D" className="opacity-90" />
            <div className="absolute bottom-0 left-0 right-0 px-6 py-5 border-t border-[#15243D]/10 bg-[#EBE8E0]/95">
              <p className="text-xs tracking-[0.22em] uppercase text-[#15243D]/70 text-center">
                28 Days · 4 Weeks · 1 Practice
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
